import { useState, useEffect, useCallback, useRef } from 'react';
import { MapLocationData } from '@hero/web-components';
import { useAddressApi } from '@hero/api-hooks';

export interface UseMapCurrentLocationOptions {
  /** Whether to show user's current location */
  showUserCurrentLocation: boolean;
  /** Whether Google Maps API is loaded */
  isLoaded: boolean;
  /** Map instance */
  map: google.maps.Map | null;
}

/**
 * Hook for managing user's current geolocation.
 * Auto-fetches user's location when enabled. Uses useAddressApi() internally for address lookup.
 */
export function useMapCurrentLocation({
  showUserCurrentLocation,
  isLoaded,
  map,
}: UseMapCurrentLocationOptions) {
  const addressApi = useAddressApi();
  const [currentLocation, setCurrentLocation] = useState<MapLocationData | null>(null);
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] = useState(false);
  /**
   * One auto-locate per mount. Without this, a device that answers
   * `POSITION_UNAVAILABLE` immediately (desktop with no location provider)
   * spins: clearing `isGettingCurrentLocation` in the error handler is itself
   * a dependency change, so the effect re-arms and asks again — thousands of
   * times a minute, re-rendering the whole map with it.
   */
  const hasAutoLocatedRef = useRef(false);

  // Callback to update current location (for MapSearch)
  const onCurrentLocationChange = useCallback((location: MapLocationData | null) => {
    setCurrentLocation(location);
  }, []);

  // Automatically get current location when showUserCurrentLocation is true
  useEffect(() => {
    if (
      showUserCurrentLocation &&
      isLoaded &&
      map &&
      !currentLocation &&
      !isGettingCurrentLocation &&
      !hasAutoLocatedRef.current
    ) {
      hasAutoLocatedRef.current = true;
      setIsGettingCurrentLocation(true);

      if (navigator.geolocation) {
        const handlePosition = async (position: GeolocationPosition) => {
          try {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const address = await addressApi.mutateAsync({ lat, lng });

            const location: MapLocationData = {
              cords: { lat, lng },
              fullAddress: address?.fullAddress || `${lat}, ${lng}`,
              city: address?.city?.name,
              address: address || undefined,
              isAccurateLocation: true,
              isLoadingAddress: false,
            };

            setCurrentLocation(location);
          } catch (error) {
            console.error('Failed to get current location:', error);
          } finally {
            setIsGettingCurrentLocation(false);
          }
        };

        // Try high accuracy first; on timeout/failure fall back to low accuracy (WiFi/IP-based, much faster).
        navigator.geolocation.getCurrentPosition(handlePosition,
          () => {
            navigator.geolocation.getCurrentPosition(handlePosition,
              error => {
                console.error('Geolocation error:', error);
                setIsGettingCurrentLocation(false);
              },
              { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
            );
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
        );
      } else {
        setIsGettingCurrentLocation(false);
      }
    }
  }, [
    showUserCurrentLocation,
    isLoaded,
    map,
    addressApi.mutateAsync,
    currentLocation,
    isGettingCurrentLocation,
  ]);

  return {
    currentLocation,
    isGettingCurrentLocation,
    onCurrentLocationChange,
  };
}
